import React, { useState, useEffect } from 'react';
import { Button, Form, Modal, Spinner, Badge, InputGroup } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { EmailSponsorService } from '../../../services/email_sponsor_service';
import { UserModel } from '../../../models/UserModel';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';

interface Props {
    show: boolean;
    onHide: () => void;
    sponsorEmail?: string; 
    sponsorName?: string;
    initialSubject?: string; 
    initialMessage?: string; 
}

const SendMailSponsor: React.FC<Props> = ({
    show,
    onHide,
    sponsorEmail = '',
    initialSubject = '',
    initialMessage = ''
}) => {
    // On récupère l'utilisateur directement depuis le store Redux (méthode A)
    const reduxUser = useSelector((state: RootState) => state.user.user) as UserModel | null;

    // État local
    const [currentUser, setCurrentUser] = useState<UserModel | null>(null);
    const [recipients, setRecipients] = useState<string[]>(() => sponsorEmail ? [sponsorEmail] : []);
    const [inputValue, setInputValue] = useState<string>('');
    const [subject, setSubject] = useState<string>('');
    const [message, setMessage] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);

    useEffect(() => {
        if (show) {
            if (!reduxUser) {
                // L'utilisateur n'est pas disponible dans le store -> on affiche une erreur et on ferme le modal.
                toast.error('Utilisateur non connecté. Impossible d\'ouvrir le modal d\'envoi.');
                onHide();
                return;
            }

            setCurrentUser(reduxUser);

            if (initialSubject && initialMessage) {
                setSubject(initialSubject);
                setMessage(initialMessage);
            } else {
                const defaultData = EmailSponsorService.generateDefaultReferralData(reduxUser);
                setSubject(defaultData.subject);
                setMessage(defaultData.message);
            }
        } else {
            // Reset à la fermeture
            resetForm();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, reduxUser, initialSubject, initialMessage]);

    const resetForm = () => {
        setRecipients(sponsorEmail ? [sponsorEmail] : []);
        setSubject('');
        setMessage('');
        setInputValue('');
        setIsLoading(false);
    };

    const addRecipient = (raw: string) => {
        const email = raw.trim();
        if (!email) return;

        if (!EmailSponsorService.validateEmail(email)) {
            toast.error(`Adresse invalide : ${email}`);
            return;
        }

        if (recipients.includes(email)) {
            toast.info(`Adresse déjà ajoutée : ${email}`);
            return;
        }

        setRecipients(prev => [...prev, email]);
        setInputValue('');
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            addRecipient(inputValue);
        } else if (e.key === ',') {
            e.preventDefault();
            addRecipient(inputValue.replace(',', ''));
        }
    };

    const removeRecipient = (email: string) => {
        setRecipients(prev => prev.filter(r => r !== email));
    };

    const handleAddClick = () => {
        addRecipient(inputValue);
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (recipients.length === 0) {
            toast.error('Veuillez ajouter au moins une adresse e-mail.');
            return;
        }

        if (!subject.trim() || !message.trim()) {
            toast.error('Veuillez saisir un sujet et un message.');
            return;
        }

        if (!currentUser) {
            toast.error("Utilisateur non connecté. Impossible d'envoyer l'e-mail.");
            return;
        }

        setIsLoading(true);

        try {
            const emailRecipients = recipients.map(email => ({ email }));
            console.log('Envoi email - payload:', { sender: currentUser, recipients: emailRecipients, subject, message });

            const result = await EmailSponsorService.sendReferralEmails(currentUser, {
                recipients: emailRecipients,
                customMessage: message,
                customSubject: subject
            });

            if (result.success) {
                toast.success(result.message);
                onHide();
                resetForm();
            } else {
                toast.error('Aucun email n\'a pu être envoyé. Vérifiez les adresses destinataires.');
            }

        } catch (error: any) {
            const msg = error?.message || "Erreur lors de l'envoi de l'e-mail de parrainage";
            toast.error(msg);
            console.error('SendReferralEmails error:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const getUserDisplayName = () => {
        if (!currentUser) return '';

        if (currentUser.user_type === 'entreprise' || currentUser.is_entreprise) {
            return `${currentUser.raison_social || currentUser.name} (${currentUser.email})`;
        } else {
            return `${currentUser.firstname} ${currentUser.name} (${currentUser.email})`;
        }
    };

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton>
                <Modal.Title>Envoyer mon code de parrainage</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {currentUser && (
                    <div className="mb-3 p-2 bg-light rounded">
                        <small className="text-muted">
                            <strong>Expéditeur :</strong> {getUserDisplayName()}
                        </small>
                    </div>
                )}

                <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-3">
                        <Form.Label>Destinataire(s)</Form.Label>
                        <div style={{ marginBottom: 8 }}>
                            {recipients.map(r => (
                                <Badge key={r} pill bg="secondary" className="me-2 mb-2" style={{ fontSize: '0.9em' }}>
                                    {r}
                                    <span
                                        onClick={() => removeRecipient(r)}
                                        style={{ cursor: 'pointer', marginLeft: 8, fontWeight: 700 }}
                                        aria-hidden
                                    >
                                        ×
                                    </span>
                                </Badge>
                            ))}
                        </div>

                        <InputGroup>
                            <Form.Control
                                type="email"
                                placeholder="Saisir une adresse et appuyer sur Entrée ou sur Ajouter"
                                value={inputValue}
                                onChange={e => setInputValue(e.target.value)}
                                onKeyDown={handleKeyDown}
                            />
                            <Button variant="outline-secondary" onClick={handleAddClick}>Ajouter</Button>
                        </InputGroup>
                    </Form.Group>

                    <Form.Group controlId="subject" className="mb-3">
                        <Form.Label>Sujet</Form.Label>
                        <Form.Control
                            type="text"
                            value={subject}
                            onChange={e => setSubject(e.target.value)}
                            placeholder="Sujet de l'e-mail"
                            required
                        />
                    </Form.Group>

                    <Form.Group controlId="message" className="mb-3">
                        <Form.Label>Message</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={8}
                            value={message}
                            onChange={e => setMessage(e.target.value)}
                            placeholder="Votre message..."
                            required
                        />
                        <Form.Text className="text-muted">
                            Le lien de parrainage sera automatiquement inclus dans votre message.
                        </Form.Text>
                    </Form.Group>

                    <div className="d-flex justify-content-end">
                        <Button variant="secondary" onClick={onHide} disabled={isLoading} className="me-2">
                            Annuler
                        </Button>
                        <Button
                            variant="primary"
                            type="submit"
                            disabled={isLoading || recipients.length === 0 || !subject.trim() || !message.trim()}
                        >
                            {isLoading ? (
                                <>
                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                                    Envoi...
                                </>
                            ) : 'Envoyer'}
                        </Button>
                    </div>
                </Form>
            </Modal.Body>
        </Modal>
    );
};

export default SendMailSponsor;
